import { useContext, useEffect, useState } from 'react';
import { Box, Chip, Container, IconButton } from '@mui/material';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/AppContext';
import { SttFullDialog } from '../../../sporttia/dialogs/SttFullDialog';
import { SttChipUnlocker } from '../../../sporttia/chips/SttChipUnlocker';
import { SttTable, SttTopControls } from '../../../sporttia/all';
import { useToggle } from '../../../lib/hooks';

type Props = {
    openStatus?: boolean;
    onClose: () => void;
};

type Filters = {
    code: string;
    mshipName: string;
};

type Key = {
    code: string;
    type: string;
    user?: {
        fullName?: string;
    };
};

export function DevicesKeysDialog({
    openStatus = false,
    onClose,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;

    const [keys, setKeys] = useState<{ data: Key[]; total: number } | null>(
        null,
    );

    // Trash hook
    const [trash, toggleTrash] = useToggle(() => {
        loadKeys();
    });

    const [filters, setFilter] = useState<Filters>({
        code: '',
        mshipName: '',
    });

    // Initial type
    const [keyType, setKeyType] = useState<string>('ALL');

    // Map keyType to numeric value for select
    const keyTypeToNumber: Record<string, number> = {
        ALL: 0,
        CARD: 1,
        PIN: 2,
        QR: 3,
    };

    // On keyType change, reload data
    useEffect(() => {
        loadKeys();
    }, [keyType]);

    /**
     * Load keys
     */
    const loadKeys = (p = { page: 1, rows: 10 }): void => {
        const finalFilters = {
            code: filters.code,
            type: keyType === 'ALL' ? '' : keyType,
            mshipName: filters.mshipName,
        };

        cxt?.api('GET', `/scs/${cxt?.sc?.id}/unlockers`, {
            params: {
                page: p.page,
                rows: p.rows,
                ...finalFilters,
                trash,
            },
            version: 'v7',
            success: (r: { data: Key[]; total: number }) => {
                setKeys(r);
            },
        });
    };

    // -----| Render |-----
    return (
        <SttFullDialog
            open={openStatus}
            onClose={onClose}
            title={t('Unlockers.title')}
        >
            <Container>
                <Box my={3}>
                    <Paper>
                        <SttTopControls
                            p={2}
                            mainAction={{
                                type: 'component',
                                component: (
                                    <IconButton onClick={() => loadKeys()}>
                                        <SearchIcon />
                                    </IconButton>
                                ),
                            }}
                            fields={[
                                {
                                    caption: t('Code'),
                                    type: 'text',
                                    name: 'code',
                                    value: filters.code,
                                    xs: 12,
                                    sm: 4,
                                    md: 3,
                                    lg: 3,
                                    onEnterKey: loadKeys,
                                },
                                {
                                    caption: t('Type'),
                                    type: 'select',
                                    name: 'type',
                                    value: String(keyTypeToNumber[keyType]),
                                    xs: 12,
                                    sm: 4,
                                    md: 3,
                                    lg: 3,
                                    options: [
                                        {
                                            label: t('All'),
                                            value: 0,
                                        },
                                        {
                                            label: t('UT.CARD'),
                                            value: 1,
                                        },
                                        {
                                            label: t('UT.PIN'),
                                            value: 2,
                                        },
                                        {
                                            label: t('UT.QR'),
                                            value: 3,
                                        },
                                    ],
                                    mandatory: false,
                                    cleanable: false,
                                    onChange: ({
                                        value,
                                    }: {
                                        value: unknown;
                                    }) => {
                                        const valueMap: Record<number, string> =
                                            {
                                                0: 'ALL',
                                                1: 'CARD',
                                                2: 'PIN',
                                                3: 'QR',
                                            };
                                        setKeyType(
                                            valueMap[value as number] || 'ALL',
                                        );
                                    },
                                },
                                {
                                    caption: t('Name'),
                                    type: 'text',
                                    name: 'mshipName',
                                    value: filters.mshipName,
                                    xs: 12,
                                    sm: 4,
                                    md: 3,
                                    lg: 3,
                                    onEnterKey: loadKeys,
                                },
                            ]}
                            onToggleTrash={toggleTrash}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setFilter({
                                    ...filters,
                                    [name]: value as string,
                                });
                            }}
                            onFilter={loadKeys}
                        />

                        <SttTable
                            onFetch={loadKeys}
                            data={keys}
                            columns={[
                                {
                                    title: t('Code'),
                                    type: 'id',
                                    field: 'code',
                                },
                                {
                                    title: t('Type'),
                                    value: (row: Key) => (
                                        <SttChipUnlocker
                                            unlocker={{
                                                id: 0,
                                                type: row.type as any,
                                            }}
                                        />
                                    ),
                                },
                                {
                                    align: 'left',
                                    title: t('User'),
                                    value: (row: Key) =>
                                        row.user?.fullName ? (
                                            <Chip
                                                size="small"
                                                label={row.user.fullName}
                                            />
                                        ) : null,
                                },
                            ]}
                        />
                    </Paper>
                </Box>
            </Container>
        </SttFullDialog>
    );
}
